import DOMPurify from "dompurify";
import { isFormatClass } from "./richFormat";

/**
 * Makes stored message HTML safe to render.
 *
 * Message bodies are user-supplied and the BFF stores them exactly as sent,
 * so a member could post `<img src=x onerror=…>` and run script in everyone
 * else's session. Every `dangerouslySetInnerHTML` of message content goes
 * through here: formatting survives; scripts, event handlers, iframes, forms,
 * inline styles and links other than http(s)/mailto/relative do not.
 */
const ALLOWED_TAGS = [
    "a", "b", "strong", "i", "em", "u", "s", "del", "ins", "mark", "small", "sub", "sup",
    "p", "br", "hr", "span", "div", "blockquote", "code", "pre",
    "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
    "table", "thead", "tbody", "tr", "th", "td", "img"
];
const ALLOWED_ATTR = ["href", "title", "alt", "src", "class", "target", "rel", "colspan", "rowspan"];
const ALLOWED_URI = /^(?:https?:|mailto:|data:image\/(?:png|gif|jpe?g|webp);base64,|\/(?!\/)|#)/i;

/**
 * `class` is kept only for values the app itself writes, because a message author must not be able to
 * pull in any Tailwind utility (`fixed inset-0 z-50` would cover the page):
 *  - fg-c-<id>, fg-h-<id>, fg-s-<id>: composer colour / highlight / size (ids from Shared/richformat/format.json);
 *  - fg-mention, fg-mention-me: @mention chips (lib/mentions.ts);
 *  - language-<name>: a code block's language;
 *  - the exact class strings that lib/markdown.ts and the quote / forward / meeting-card builders have
 *    always written into stored messages (older messages carry them, so they keep their look).
 * Any other class token is dropped; when none is left the attribute goes.
 */
const KEPT_TOKENS = new Set(["fg-mention", "fg-mention-me"]);
const LANGUAGE_TOKEN = /^language-[A-Za-z0-9_+#.-]{1,32}$/;
export const LEGACY_CLASS_VALUES: ReadonlySet<string> = new Set([
    "rounded bg-muted px-3 py-2 text-xs font-mono overflow-x-auto my-1",
    "border-l-4 border-muted-foreground/40 pl-3 text-muted-foreground my-1",
    "list-disc pl-5 my-1 space-y-0.5",
    "list-decimal pl-5 my-1 space-y-0.5",
    "my-2 border-border",
    "my-0.5",
    "rounded bg-muted px-1 font-mono text-[0.85em]",
    "text-primary underline",
    "max-w-xs rounded-md",
    "rounded-md border bg-muted/40 p-3",
    "border-l-4 border-primary/60 pl-2 text-xs text-muted-foreground",
    "text-xs text-muted-foreground"
]);

/** The class value to keep for `value`, or "" to drop the attribute. */
export function allowedClassValue(value: string): string {
    const trimmed = value.trim().replace(/\s+/g, " ");
    if (LEGACY_CLASS_VALUES.has(trimmed)) return trimmed;
    return trimmed
        .split(" ")
        .filter((t) => isFormatClass(t) || KEPT_TOKENS.has(t) || LANGUAGE_TOKEN.test(t))
        .join(" ");
}

let hooked = false;

function ensureLinkHook() {
    if (hooked) return;
    hooked = true;
    // Links open in a new tab without giving the target page a handle on ours.
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A") {
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer");
        }
        const cls = node.getAttribute?.("class");
        if (cls !== null && cls !== undefined) {
            const kept = allowedClassValue(cls);
            if (kept) node.setAttribute("class", kept);
            else node.removeAttribute("class");
        }
    });
}

export function sanitizeHtml(html: string | null | undefined): string {
    if (!html) return "";
    ensureLinkHook();
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOWED_URI_REGEXP: ALLOWED_URI
    });
}
